// src/components/TeamItemsPage/QuickViewPanel.jsx

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import styles from './QuickViewPanel.module.css';
import { 
  FaTimes, FaEdit, FaTrash, FaCheckCircle, FaFlag, FaCalendarAlt, 
  FaUser, FaComments, FaPaperPlane 
} from 'react-icons/fa';
import { format } from 'date-fns';

const QuickViewPanel = ({ 
  item, 
  onClose, 
  onEdit, 
  onDelete, 
  onMarkComplete,
  onStatusChange,
  canManage,
  canComplete = false, // Allows assigned users to change status
  comments = [], 
  onAddComment,
  onEditComment,
  onDeleteComment,
  currentUserId
}) => {
  const [newComment, setNewComment] = useState('');
  const [localStatus, setLocalStatus] = useState(item.status);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');

  const handleStatusChange = async (newStatus) => {
    setLocalStatus(newStatus);
    // Create updated item with new status
    const updatedItem = { ...item, status: newStatus };
    onStatusChange(updatedItem);
  };

  const handleAddComment = async () => {
    if (newComment.trim()) {
      await onAddComment(newComment.trim());
      setNewComment('');
    }
  };

  const startEditComment = (comment) => {
    setEditingCommentId(comment._id);
    setEditingCommentText(comment.text);
  };

  const saveEditComment = async () => {
    if (editingCommentText.trim() && editingCommentId) {
      const success = await onEditComment(editingCommentId, editingCommentText.trim());
      if (success) {
        setEditingCommentId(null);
        setEditingCommentText('');
      }
    }
  };

  const cancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentText('');
  };

  const handleDeleteComment = async (commentId) => {
    const success = await onDeleteComment(commentId);
    if (success && editingCommentId === commentId) {
      setEditingCommentId(null);
      setEditingCommentText('');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return '#dc3545';
      case 'Medium': return '#ffc107';
      case 'Low': return '#28a745';
      default: return '#6c757d';
    }
  };

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.title}>{item.title}</h2>
          <button className={styles.closeBtn} onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {/* Priority and Status */}
          <div className={styles.row}>
            <div className={styles.field}>
              <label className={styles.label}>
                <FaFlag /> Priority
              </label>
              <div
                className={styles.priorityBadge}
                style={{ backgroundColor: getPriorityColor(item.priority), color: '#fff' }}
              >
                {item.priority}
              </div>
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Status</label>
              {(canManage || canComplete) ? (
                <select
                  value={localStatus}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className={styles.statusSelect}
                >
                  <option value="To Do">To Do</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Done">Done</option>
                </select>
              ) : (
                <div className={styles.statusBadge}>{localStatus}</div>
              )}
            </div>
          </div>

          {/* Due Date and Assignee */}
          <div className={styles.row}>
            {item.dueDate && (
              <div className={styles.field}>
                <label className={styles.label}>
                  <FaCalendarAlt /> Due Date
                </label>
                <div className={styles.value}>
                  {format(new Date(item.dueDate), 'PPP')}
                </div>
              </div>
            )}
            {item.assignedTo && (
              <div className={styles.field}>
                <label className={styles.label}>
                  <FaUser /> Assigned To
                </label>
                <div className={styles.value}>
                  {item.assignedTo.firstName} {item.assignedTo.lastName}
                </div>
              </div>
            )}
          </div>

          {/* Event */}
          {item.event && (
            <div className={styles.field}>
              <label className={styles.label}>Event</label>
              <div className={styles.value}>{item.event.title}</div>
            </div>
          )}

          {/* Description */}
          {item.description && (
            <div className={styles.field}>
              <label className={styles.label}>Description</label>
              <div className={styles.description}>{item.description}</div>
            </div>
          )}

          {/* Tags */}
          {item.tags && item.tags.length > 0 && (
            <div className={styles.field}>
              <label className={styles.label}>Tags</label>
              <div className={styles.tags}>
                {item.tags.map((tag, index) => (
                  <span key={index} className={styles.tag}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Comments Section */}
          <div className={styles.commentsSection}>
            <label className={styles.label}>
              <FaComments /> Comments ({comments.length})
            </label>
            
            <div className={styles.commentsList}>
              {comments.length === 0 ? (
                <div className={styles.noComments}>No comments yet</div>
              ) : (
                comments.map((comment) => {
                  const isEditing = editingCommentId === comment._id;
                  const isAuthor = currentUserId && comment.author?._id === currentUserId;
                  const authorName = comment.author 
                    ? `${comment.author.firstName || ''} ${comment.author.lastName || ''}`.trim() || comment.author.email
                    : 'Unknown';

                  return (
                    <div key={comment._id} className={styles.comment}>
                      <div className={styles.commentHeader}>
                        <strong className={styles.commentAuthor}>{authorName}</strong>
                        <span className={styles.commentTime}>
                          {format(new Date(comment.createdAt), 'MMM dd, yyyy h:mm a')}
                          {comment.isEdited && <span className={styles.editedBadge}> (edited)</span>}
                        </span>
                      </div>
                      
                      {isEditing ? (
                        <div className={styles.editCommentForm}>
                          <textarea
                            value={editingCommentText}
                            onChange={(e) => setEditingCommentText(e.target.value)}
                            className={styles.editCommentInput}
                            rows={3}
                          />
                          <div className={styles.editCommentActions}>
                            <button onClick={saveEditComment} className={styles.saveBtn}>
                              Save
                            </button>
                            <button onClick={cancelEditComment} className={styles.cancelBtn}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className={styles.commentText}>{comment.text}</div>
                          {isAuthor && (
                            <div className={styles.commentActions}>
                              <button 
                                onClick={() => startEditComment(comment)} 
                                className={styles.commentActionBtn}
                                title="Edit comment"
                              >
                                <FaEdit /> Edit
                              </button>
                              <button 
                                onClick={() => handleDeleteComment(comment._id)} 
                                className={styles.commentActionBtn}
                                title="Delete comment"
                              >
                                <FaTrash /> Delete
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            <div className={styles.addComment}>
              <input
                type="text"
                placeholder="Add a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                className={styles.commentInput}
              />
              <button
                onClick={handleAddComment}
                className={styles.commentBtn}
                disabled={!newComment.trim()}
              >
                <FaPaperPlane />
              </button>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className={styles.footer}>
          {(canManage || canComplete) && item.status !== 'Done' && (
            <button
              className={styles.completeBtn}
              onClick={onMarkComplete}
            >
              <FaCheckCircle /> Mark Complete
            </button>
          )}
          {canManage && (
            <div className={styles.footerActions}>
              <button className={`${styles.actionBtn} ${styles.editBtn}`} onClick={onEdit}>
                <FaEdit /> Edit
              </button>
              <button className={`${styles.actionBtn} ${styles.deleteBtn}`} onClick={onDelete}>
                <FaTrash /> Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

QuickViewPanel.propTypes = {
  item: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onMarkComplete: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  canManage: PropTypes.bool,
  comments: PropTypes.array,
  onAddComment: PropTypes.func.isRequired,
  onEditComment: PropTypes.func,
  onDeleteComment: PropTypes.func,
  currentUserId: PropTypes.string
};

export default QuickViewPanel;

